import { useMemo, useState } from 'react'
import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { AppIcon } from '../ui/icon'
import { mdiMagnify, mdiOpenInNew } from '@mdi/js'
import { useLocale } from '../../contexts/LocaleContext'
import { useFormatters } from '../../lib/format'
import { Input } from '../ui/input'
import { Badge, badgeVariants } from '../ui/badge'
import { Button } from '../ui/button'
import { Skeleton } from '../ui/skeleton'
import { CategoryCards } from '../steering/CategoryCards'
import { $api } from '../../api/queryClient'
import {
    useGroupMatrix,
    useGroupPerimeter,
    useGroupSummary,
    useGroupTrend,
    type GroupException,
    type GroupKPIMeta,
    type GroupMatrixRow,
    type Level,
    type Period,
    type Tier,
} from '../../api/groupSteering'
import { TIER_TEXT, TIER_VAR, formatBytes, formatDelta, formatUnit, gaugeRatio, langOf, text, tierOf } from './format'
import { KpiPills, ReadingBlock, TierBadge, tierBound } from './primitives'
import { TrendChart } from './charts'
import { PageHeader } from '../shared/PageHeader'
import { SECTION_ICONS } from '../../lib/section-icons'
import type { SteeringCategory, TicketCategory } from '../steering/steering-categories'
import { EmptyState } from '../ui/empty-state'

/**
 * Master–detail per perimeter (pattern §27 adapted): the list on the left,
 * alphabetical and never ranked, valued on the family's star; the sheet on
 * the right with its tier badge and bound, three cells, four bullets, the
 * monthly series against the peers' median, the exceptions, where the work
 * is, and the sites.
 */
export function PerimetersView({
    org,
    period,
    level,
    family,
    selected,
    q,
    onSelect,
    onQuery,
    onFamilyChange,
}: {
    org: string
    period: Period
    level: Level
    family: string
    selected: string | undefined
    q: string
    onSelect: (uid: string | undefined) => void
    onQuery: (q: string) => void
    onFamilyChange: (family: string) => void
}) {
    const { t } = useTranslation('group-steering')
    const { locale } = useLocale()
    const lang = langOf(locale)
    const summary = useGroupSummary(org, period, level)
    const matrix = useGroupMatrix(org, period, level, family)
    const families = (summary.data?.families ?? []).filter((f) => f.tiles.length > 0)
    const star = matrix.data?.kpis.find((k) => k.pilot && k.family === family) ?? matrix.data?.kpis[0]
    const rows = useMemo(() => {
        const all = matrix.data?.rows ?? []
        const needle = q.trim().toLowerCase()
        return needle ? all.filter((r) => (r.perimeter.name ?? '').toLowerCase().includes(needle) || (r.perimeter.code ?? '').toLowerCase().includes(needle)) : all
    }, [matrix.data, q])
    const total = matrix.data?.rows.length ?? 0

    return (
        <div className="space-y-4" data-testid="group-perimeters">
            <PageHeader
                icon={SECTION_ICONS.assets}
                title={t('perimeters.title')}
                description={t('perimeters.intro')}
                data-testid="group-perimeters-title"
            />
            <div className="flex flex-wrap gap-1.5" role="radiogroup" data-testid="group-perimeters-family">
                {families.map((f) => (
                    <button
                        key={f.code}
                        type="button"
                        role="radio"
                        aria-checked={f.code === family}
                        onClick={() => onFamilyChange(f.code)}
                        className={`px-2 py-0.5 rounded-full border text-xs ${f.code === family ? 'bg-primary text-primary-foreground border-primary' : 'text-muted-foreground hover:text-foreground'}`}
                        data-testid={`group-perimeters-family-${f.code}`}
                    >
                        {text(f.label, lang)}
                    </button>
                ))}
            </div>
            <div className="grid lg:grid-cols-[20rem_1fr] gap-4">
                <aside className="rounded-lg border bg-card" data-testid="perimeter-list">
                    <div className="p-2 border-b space-y-1">
                        <div className="relative">
                            <AppIcon tone="muted" path={mdiMagnify} size="xs" className="absolute left-2 top-2.5" />
                            <Input value={q} onChange={(e) => onQuery(e.target.value)} placeholder={t('perimeters.search')} className="pl-7 h-8 text-sm" data-testid="perimeter-search" />
                        </div>
                        <div className="text-[11px] text-muted-foreground px-1">
                            {star ? t('perimeters.count', { count: rows.length, total, kpi: text(star.label, lang) }) : `${rows.length} / ${total}`}
                        </div>
                    </div>
                    {matrix.isLoading ? (
                        <div className="p-3 space-y-2">
                            <Skeleton className="h-10" />
                            <Skeleton className="h-10" />
                        </div>
                    ) : (
                        <ul className="max-h-[70vh] overflow-y-auto">
                            {rows.map((r) => (
                                <PerimeterRow key={r.perimeter.uid ?? r.perimeter.name} row={r} star={star} selected={!!r.perimeter.uid && r.perimeter.uid === selected} onSelect={() => r.perimeter.uid && onSelect(r.perimeter.uid)} />
                            ))}
                        </ul>
                    )}
                </aside>
                <div>
                    {selected ? (
                        <PerimeterSheet key={`${selected}/${family}`} org={org} uid={selected} period={period} level={level} family={family} />
                    ) : (
                        <EmptyState title={t('perimeters.select')} data-testid="perimeter-sheet-empty" />
                    )}
                </div>
            </div>
        </div>
    )
}

function PerimeterRow({ row, star, selected, onSelect }: { row: GroupMatrixRow; star: GroupKPIMeta | undefined; selected: boolean; onSelect: () => void }) {
    const { t } = useTranslation('group-steering')
    const { locale } = useLocale()
    const lang = langOf(locale)
    const cell = star ? row.values[star.code] : undefined
    const tier = tierOf(cell)
    const gauge = star ? gaugeRatio(cell?.value, star.unit) : null
    const name = row.perimeter.anonymous ? t('perimeters.anonymous', { kind: t(`level.${row.perimeter.kind}`) }) : row.perimeter.name || row.perimeter.code
    return (
        <li>
            <button
                type="button"
                onClick={onSelect}
                disabled={row.perimeter.anonymous}
                aria-pressed={selected}
                className={`w-full text-left px-3 py-2 border-b last:border-b-0 ${selected ? 'bg-primary/5 border-l-2 border-l-primary' : 'hover:bg-muted/40'} ${row.perimeter.anonymous ? 'opacity-60 cursor-default' : ''}`}
                data-testid={`perimeter-row-${row.perimeter.uid ?? 'anonymous'}`}
            >
                <div className="flex items-center justify-between gap-2">
                    <span className="text-sm text-foreground truncate">{name}</span>
                    <span className={`text-sm tabular-nums font-medium ${TIER_TEXT[tier]}`}>{star && cell && !cell.suppressed ? formatUnit(cell.value, star.unit, lang, locale) : '—'}</span>
                </div>
                <div className="text-[11px] text-muted-foreground truncate">
                    {row.context ? t('perimeters.context', { units: row.context.units, buildings: row.context.buildings, sites: row.context.sites }) : ''}
                </div>
                {gauge !== null && (
                    <div className="mt-1 h-1 w-full rounded bg-muted overflow-hidden" aria-hidden>
                        <div className="h-full" style={{ width: `${gauge * 100}%`, background: `var(${TIER_VAR[tier]})` }} />
                    </div>
                )}
            </button>
        </li>
    )
}

export function PerimeterSheet({ org, uid, period, level, family }: { org: string; uid: string; period: Period; level: Level; family: string }) {
    const { t } = useTranslation('group-steering')
    const { t: tLeases } = useTranslation('leases')
    const { locale } = useLocale()
    const lang = langOf(locale)
    const { formatDate } = useFormatters()
    const sheet = useGroupPerimeter(org, uid, period, family)
    const [kpi, setKpi] = useState<string | undefined>(undefined)
    const cells = sheet.data?.cells ?? []
    const activeCode = kpi ?? cells[0]?.kpi.code
    const trend = useGroupTrend(org, activeCode, level, uid)
    const activeKpi = cells.find((c) => c.kpi.code === activeCode)?.kpi
    const stats = $api.useQuery(
        'get',
        '/api/v1/organizations/{orgCode}/issues/stats',
        { params: { path: { orgCode: org }, query: { group_by: 'category', status: 'open', assignee_uid: 'all', asset_subtree_uid: uid } } },
        { enabled: !!uid },
    )
    const urgent = $api.useQuery(
        'get',
        '/api/v1/organizations/{orgCode}/issues/stats',
        { params: { path: { orgCode: org }, query: { group_by: 'category', status: 'open', priority: '1,2', assignee_uid: 'all', asset_subtree_uid: uid } } },
        { enabled: !!uid },
    )
    const toCounts = (data: { data?: { category?: string | null; count?: number | null }[] } | undefined): Partial<Record<TicketCategory, number>> => {
        const out: Partial<Record<TicketCategory, number>> = {}
        for (const r of data?.data ?? []) if (r.category) out[r.category as TicketCategory] = r.count ?? 0
        return out
    }

    if (sheet.isLoading) return <Skeleton className="h-64 w-full" />
    if (!sheet.data) return <div className="rounded-lg border p-6 text-sm text-muted-foreground">{t('space.no_console_body')}</div>
    const s = sheet.data
    const starCell = cells[0]
    const starTier = (s.star_tier as Tier) || 'none'

    return (
        <div className="space-y-4" data-testid="perimeter-sheet">
            <header className="rounded-lg border bg-card p-4">
                <div className="flex flex-wrap items-start justify-between gap-2">
                    <div>
                        <div className="text-lg font-semibold text-foreground" data-testid="perimeter-sheet-name">
                            {s.perimeter.name}
                        </div>
                        <div className="text-xs text-muted-foreground">
                            {t(`level.${s.perimeter.kind}`)}
                            {s.parent?.name ? ` · ${t('perimeters.parent', { name: s.parent.name })}` : ''}
                            {' · '}
                            {t('perimeters.context', { units: s.context.units, buildings: s.context.buildings, sites: s.context.sites })}
                            {s.context.building_age_median !== undefined ? ` · ${t('perimeters.age', { years: s.context.building_age_median })}` : ''}
                        </div>
                    </div>
                    {starCell && <TierBadge tier={starTier} bound={tierBound(starCell.kpi, starTier, lang, locale)} testId="perimeter-sheet-tier" />}
                </div>
                <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-2" data-testid="perimeter-sheet-cells">
                    {cells.slice(0, 3).map((c) => {
                        const tier = tierOf(c.cell)
                        return (
                            <div key={c.kpi.code} className="rounded-md border p-2.5">
                                <div className="text-xs font-medium text-foreground truncate">
                                    {c.kpi.pilot ? '★ ' : ''}
                                    {text(c.kpi.label, lang)}
                                </div>
                                <div className="text-[11px] text-muted-foreground truncate">{text(c.kpi.denominator, lang)}</div>
                                <div className={`mt-1 text-xl font-semibold tabular-nums ${TIER_TEXT[tier]}`}>{c.cell.suppressed ? '—' : formatUnit(c.cell.value, c.kpi.unit, lang, locale)}</div>
                                <div className="text-[11px] text-muted-foreground">
                                    {t('consolidated.n', { count: c.cell.n })}
                                    {c.cell.coverage !== undefined && c.cell.coverage < 0.8 ? ` · ⚠ ${t('consolidated.coverage', { value: `${Math.round(c.cell.coverage * 100)} %` })}` : ''}
                                </div>
                            </div>
                        )
                    })}
                </div>
                <ul className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2 text-sm" data-testid="perimeter-sheet-bullets">
                    <li><span className="font-medium tabular-nums text-foreground">{starCell ? formatDelta(s.bullets.delta_vs_peers, starCell.kpi.unit, lang, locale) || '—' : '—'}</span> <span className="text-muted-foreground">{t('perimeters.bullet_peers')}</span></li>
                    <li><span className="font-medium tabular-nums text-foreground">{starCell ? formatDelta(s.bullets.delta_12m, starCell.kpi.unit, lang, locale) || '—' : '—'}</span> <span className="text-muted-foreground">{t('perimeters.bullet_12m')}</span></li>
                    <li><span className="font-medium tabular-nums text-foreground">{s.bullets.exceptions_count}</span> <span className="text-muted-foreground">{t('perimeters.bullet_exceptions')}</span></li>
                    <li><span className="font-medium tabular-nums text-foreground">{s.bullets.handovers_12m}</span> <span className="text-muted-foreground">{t('perimeters.bullet_handovers')}</span></li>
                </ul>
            </header>

            <section className="rounded-lg border bg-card p-4 space-y-3" data-testid="perimeter-sheet-series">
                <div className="font-medium text-foreground">{t('perimeters.series_title')}</div>
                <KpiPills kpis={cells.map((c) => c.kpi)} value={activeCode ?? ''} onChange={setKpi} lang={lang} />
                {trend.data && activeKpi ? <TrendChart trend={trend.data} kpi={activeKpi} /> : <Skeleton className="h-[260px] w-full" />}
                <p className="text-[11px] text-muted-foreground">{t('perimeters.series_note')}</p>
                {trend.data && <ReadingBlock reading={trend.data.reading} />}
            </section>

            <section className="rounded-lg border bg-card p-4" data-testid="perimeter-sheet-exceptions">
                <div className="font-medium text-foreground mb-2">{t('perimeters.exceptions_title')}</div>
                {s.exceptions.length === 0 ? (
                    <EmptyState variant="inline" title={t('perimeters.no_exceptions')} />
                ) : (
                    <ExceptionsTable rows={s.exceptions} compact />
                )}
            </section>

            <section className="rounded-lg border bg-card p-4" data-testid="perimeter-sheet-work">
                <div className="font-medium text-foreground mb-2">{t('perimeters.work_title')}</div>
                <CategoryCards counts={toCounts(stats.data as never)} urgentCounts={toCounts(urgent.data as never)} loading={stats.isLoading} onSelect={(category: SteeringCategory) => {
                    window.location.assign(`/${org}/steering?category=${category}&asset=${uid}`)
                }} />
                <Button variant="outline" size="sm" asChild data-testid="perimeter-sheet-open-steering">
                    <Link to="/$org/steering" params={{ org }} search={{ asset: uid, category: undefined, view: undefined, selected: undefined, subcategory: undefined, subtree: undefined, due: undefined, status: undefined, sort: undefined, q: undefined, page: undefined, tab: undefined, nature: undefined }}>
                        <AppIcon path={mdiOpenInNew} size="sm" className="mr-1" />
                        {t('perimeters.open_steering', { name: s.perimeter.name })}
                    </Link>
                </Button>
                {family === 'rental' && s.perimeter.kind !== 'territory' && (
                    <Button variant="outline" size="sm" asChild className="ml-2" data-testid="perimeter-sheet-open-leases">
                        <Link to="/$org/leases" params={{ org }} search={{ under: uid, regime: undefined, ends_before: undefined, status: undefined, page: undefined }}>
                            <AppIcon path={mdiOpenInNew} size="sm" className="mr-1" />
                            {tLeases('steering_link.title')}
                        </Link>
                    </Button>
                )}
            </section>

            <footer className="rounded-lg border bg-card p-4 text-xs text-muted-foreground space-y-2" data-testid="perimeter-sheet-sites">
                <div className="font-medium text-foreground text-sm">{t('perimeters.sites_title')}</div>
                <ul className="flex flex-wrap gap-1.5">
                    {s.sites.map((site) => (
                        <li key={site.uid}>
                            <Link to="/$org/assets/$assetUid" params={{ org, assetUid: site.uid }} className={badgeVariants({ variant: 'outline' })} data-testid={`sheet-site-${site.uid}`}>
                                {site.name} · {site.units}
                                <span className="ml-1 text-muted-foreground" data-testid={`sheet-site-documents-${site.uid}`}>
                                    {'· '}
                                    {t('perimeters.site_documents', { count: site.documents, size: formatBytes(site.documents_size_bytes, locale) })}
                                </span>
                            </Link>
                        </li>
                    ))}
                </ul>
                <Link
                    to="/$org/group/sites"
                    params={{ org }}
                    search={{ perimeter: uid, period }}
                    className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
                    data-testid="group-perimeter-sites-link"
                >
                    <AppIcon path={mdiOpenInNew} size="xs" />
                    {t('perimeters.sites_nps_link')}
                </Link>
                <p>{t('perimeters.attribution_note')}</p>
                {s.freshness.computed_at && <p>{t('space.freshness', { date: formatDate(s.freshness.computed_at, { dateStyle: 'short', timeStyle: 'short' }) })}</p>}
            </footer>
        </div>
    )
}

/** The exceptions table — severity, perimeter, indicator, value, why, n, coverage, proposed gesture. */
export function ExceptionsTable({ rows, compact, org }: { rows: GroupException[]; compact?: boolean; org?: string }) {
    const { t } = useTranslation('group-steering')
    const { locale } = useLocale()
    const lang = langOf(locale)
    if (rows.length === 0)
        return <EmptyState variant="inline" live title={t('exceptions.empty')} data-testid="exceptions-empty" />
    return (
        <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="exceptions-table">
                <thead className="text-xs text-muted-foreground">
                    <tr className="text-left">
                        <th className="py-1 pr-2 font-medium">{t('exceptions.severity')}</th>
                        {!compact && <th className="py-1 pr-2 font-medium">{t('exceptions.perimeter')}</th>}
                        <th className="py-1 pr-2 font-medium">{t('exceptions.indicator')}</th>
                        <th className="py-1 pr-2 font-medium text-right">{t('exceptions.value')}</th>
                        <th className="py-1 pr-2 font-medium">{t('exceptions.why')}</th>
                        <th className="py-1 pr-2 font-medium text-right">{t('exceptions.n')}</th>
                        <th className="py-1 pr-2 font-medium text-right">{t('exceptions.coverage')}</th>
                        <th className="py-1 font-medium">{t('exceptions.action')}</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((r, i) => (
                        <tr key={`${r.perimeter.uid ?? i}-${r.kpi}`} className="border-t" data-testid={`exception-row-${r.kpi}`}>
                            <td className="py-1.5 pr-2">
                                <Badge variant="outline" className={r.severity === 'critical' ? 'text-destructive border-destructive/40' : 'text-[var(--chart-4)] border-[var(--chart-4)]/40'}>
                                    {t(`exceptions.${r.severity}`)}
                                </Badge>
                            </td>
                            {!compact && (
                                <td className="py-1.5 pr-2 text-foreground">
                                    {r.perimeter.uid && org ? (
                                        <Link to="/$org/group/perimeters" params={{ org }} search={{ selected: r.perimeter.uid, family: r.family, period: undefined, level: undefined, kpi: undefined, q: undefined }} className="hover:underline">
                                            {r.perimeter.name}
                                        </Link>
                                    ) : (
                                        r.perimeter.name || t('perimeters.anonymous', { kind: t(`level.${r.perimeter.kind}`) })
                                    )}
                                </td>
                            )}
                            <td className="py-1.5 pr-2 font-mono text-xs">{r.kpi}</td>
                            <td className={`py-1.5 pr-2 text-right tabular-nums font-medium ${TIER_TEXT[(r.cell.tier as Tier) || 'none']}`}>{r.cell.value === null || r.cell.value === undefined ? '—' : String(Math.round(r.cell.value * 100) / 100)}</td>
                            <td className="py-1.5 pr-2 text-muted-foreground">{text(r.why, lang)}</td>
                            <td className="py-1.5 pr-2 text-right tabular-nums text-muted-foreground">{r.cell.n}</td>
                            <td className="py-1.5 pr-2 text-right tabular-nums text-muted-foreground">{r.cell.coverage === undefined ? '—' : `${Math.round(r.cell.coverage * 100)} %`}</td>
                            <td className="py-1.5 text-muted-foreground">{text(r.action, lang)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    )
}
